// @ts-check

/**
 * @file Renders the invoices index route.
 */

import {FlashNotifications} from "flash-notifications"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {createRef, memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {useRouter} from "expo-router"
import useBreakpoint from "responsive-breakpoints/build/use-breakpoint.js"
import {View} from "react-native-propforge"

import Button from "../../src/components/button"
import EmptyState from "../../src/components/empty-state"
import InvoiceCard from "../../src/components/invoices/card"
import {Invoice} from "../../src/frontend-models/invoice"
import LabeledSelect from "../../src/components/labeled-select"
import LabeledTextInput from "../../src/components/labeled-text-input"
import LoadingState from "../../src/components/loading-state"
import OrganizationsSelect from "../../src/components/organizations/select"
import Pagination from "../../src/components/pagination"
import ScreenTitle from "../../src/components/screen-title"
import Text from "@kaspernj/api-maker/build/utils/text"
import confirmModal from "../../src/shared/confirm-modal"
import errorMessage from "../../src/shared/error-message"
import {identifierListParam, optionalRouteParam} from "../../src/shared/route-params"
import {pushQueryParamUpdates} from "on-location-changed/build/query-param-updates"
import useCurrentQueryParams from "on-location-changed/build/use-current-query-params"
import useSelectedQueryParams from "on-location-changed/build/use-selected-query-params"
import useThemeColors, {useThemeName} from "../../src/shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode, RefObject} from "react" */
/** @typedef {Record<string, string | string[] | undefined>} RouteQueryParams */
/** @typedef {import("react-native").TextInput} NativeTextInput */
/** @type {Record<string, object>} */
const styles = {}
/** @type {Record<string, object>} */
const dataSets = {}
const DEFAULT_SORT_KEY = "date_desc"
const INVOICE_CARD_PRELOAD_SELECT = {
  Invoice: ["amountCents", "amountCurrency", "creditorId", "date", "id", "invoiceNo", "invoiceType", "organizationId", "state", "title", "translatedInvoiceType", "translatedState"],
  Organization: ["id", "name"]
}
const INVOICE_CARD_SELECT = INVOICE_CARD_PRELOAD_SELECT.Invoice
const PAGE_SIZE = 30
const SEARCH_DEBOUNCE_MS = 300
const INVOICES_QUERY_PARAM_SELECTOR = [
  "creditor_ids",
  "date_from",
  "date_to",
  "invoice_type",
  "organization_ids",
  "search",
  "sort",
  "state"
]

/**
 * @typedef {"amount_asc" | "amount_desc" | "date_asc" | "date_desc" | "invoice_no_asc" | "invoice_no_desc" | "state_asc" | "type_asc"} InvoiceSortKey
 */

/**
 * @typedef {object} InvoicesIndexFilters
 * @property {string[]} creditorIds Selected creditor organization ids.
 * @property {string} dateFrom Start date filter.
 * @property {string} dateTo End date filter.
 * @property {string} invoiceTypeFilter Selected invoice type, or blank for all.
 * @property {string[]} organizationIds Selected debitor organization ids.
 * @property {string} searchQuery Invoice number/title search text.
 * @property {InvoiceSortKey} sortKey Selected sort key.
 * @property {string} stateFilter Selected invoice state, or blank for all.
 */

/** @typedef {{[key: string]: unknown}} InvoicesIndexScreenProps */

/**
 * @typedef {object} InvoicesIndexScreenState
 * @property {string[]} creditorIds Selected creditor organization ids.
 * @property {string} dateFrom Start date filter.
 * @property {string} dateTo End date filter.
 * @property {Invoice[]} invoices Loaded invoice records.
 * @property {string} invoiceDebugState Serialized test diagnostics for invoice loading.
 * @property {string} invoiceTypeFilter Selected invoice type, or blank for all.
 * @property {string} loadedFiltersKey Serialized filters used for the loaded invoice records.
 * @property {boolean} loading Whether invoices are loading.
 * @property {string[]} organizationIds Selected debitor organization ids.
 * @property {number} page Current page number.
 * @property {string} searchQuery Invoice number/title search text.
 * @property {InvoiceSortKey} sortKey Selected sort key.
 * @property {string} stateFilter Selected invoice state, or blank for all.
 * @property {{kind: "error", text: string} | null} status Screen status message.
 * @property {number} totalCount Total invoices count.
 */

/**
 * Lists invoices with route-backed filters, sorting, and pagination.
 * @augments {ShapeComponent<InvoicesIndexScreenProps, InvoicesIndexScreenState>}
 */
class InvoicesIndexScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useBreakpoint>} */
  breakpoint = this.hookField()
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {() => RouteQueryParams} */
  currentQueryParams
  /** @type {RefObject<NativeTextInput | null>} */
  dateFromInputRef = createRef()
  /** @type {ReturnType<typeof setTimeout> | null} */
  dateFromDebounceTimer = null
  /** @type {RefObject<NativeTextInput | null>} */
  dateToInputRef = createRef()
  /** @type {ReturnType<typeof setTimeout> | null} */
  dateToDebounceTimer = null
  loadRequestId = 0
  /** @type {RouteQueryParams} */
  params
  /** @type {string} */
  routeFiltersKey = ""
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {RefObject<NativeTextInput | null>} */
  searchInputRef = createRef()
  /** @type {ReturnType<typeof setTimeout> | null} */
  searchDebounceTimer = null
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {InvoicesIndexScreenState} */
  state = {
    creditorIds: /** @type {string[]} */ [],
    dateFrom: "",
    dateTo: "",
    invoiceDebugState: "",
    invoices: /** @type {Invoice[]} */ [],
    invoiceTypeFilter: "",
    loadedFiltersKey: "",
    loading: true,
    organizationIds: /** @type {string[]} */ [],
    page: 1,
    searchQuery: "",
    sortKey: DEFAULT_SORT_KEY,
    stateFilter: "",
    status: /** @type {{kind: "error", text: string} | null} */ null,
    totalCount: 0
  }

  /** Initializes translations, palette, route filters, and invoice loading. */
  setup() {
    useLocale()

    const params = /** @type {RouteQueryParams} */ (useSelectedQueryParams(INVOICES_QUERY_PARAM_SELECTOR))
    const currentQueryParams = /** @type {() => RouteQueryParams} */ (useCurrentQueryParams())
    const router = useRouter()
    const routeFilters = filtersFromParams(params)
    const routeFiltersKey = serializeFilters(routeFilters)
    this.breakpoint = useBreakpoint()
    this.colors = useThemeColors()
    this.currentQueryParams = currentQueryParams
    this.params = params
    this.router = router
    this.themeName = useThemeName()

    if (this.routeFiltersKey !== routeFiltersKey) {
      const resetPage = this.routeFiltersKey !== ""
      this.routeFiltersKey = routeFiltersKey
      this.state.creditorIds = routeFilters.creditorIds
      this.state.dateFrom = routeFilters.dateFrom
      this.state.dateTo = routeFilters.dateTo
      this.state.invoiceTypeFilter = routeFilters.invoiceTypeFilter
      this.state.organizationIds = routeFilters.organizationIds
      this.state.searchQuery = routeFilters.searchQuery
      this.state.sortKey = routeFilters.sortKey
      this.state.stateFilter = routeFilters.stateFilter
      syncTextInputValue(this.searchInputRef, routeFilters.searchQuery)
      syncTextInputValue(this.dateFromInputRef, routeFilters.dateFrom)
      syncTextInputValue(this.dateToInputRef, routeFilters.dateTo)
      if (resetPage) this.state.page = 1
    }

    const page = this.s.page
    const creditorIdsKey = this.s.creditorIds.join(",")
    const dateFrom = this.s.dateFrom
    const dateTo = this.s.dateTo
    const invoiceTypeFilter = this.s.invoiceTypeFilter
    const organizationIdsKey = this.s.organizationIds.join(",")
    const searchQuery = this.s.searchQuery
    const sortKey = this.s.sortKey
    const stateFilter = this.s.stateFilter
    useEffect(() => {
      void this.loadInvoices()
    }, [
      page,
      creditorIdsKey,
      dateFrom,
      dateTo,
      invoiceTypeFilter,
      organizationIdsKey,
      searchQuery,
      sortKey,
      stateFilter
    ])
    useEffect(() => {
      return () => {
        if (this.dateFromDebounceTimer) clearTimeout(this.dateFromDebounceTimer)
        if (this.dateToDebounceTimer) clearTimeout(this.dateToDebounceTimer)
        if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      }
    }, [])
  }

  /**
   * Renders the invoices index screen.
   * @returns {ReactNode} Invoices index content.
   */
  render() {
    const {loading, status} = this.s
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="invoicesIndexScreen"
      >
        <ScreenTitle>{_("Invoices")}</ScreenTitle>
        <View
          testID="invoicesIndexScreen/content"
        >
          <Text
            style={styles[`title-${this.tt.themeName}`] ||= {
              fontSize: 24,
              fontWeight: 700,
              marginBottom: 16
            }}
            testID="invoicesIndexScreen/title"
          >
            {_("Invoices")}
          </Text>
          {this.renderFilters()}
          {status &&
            <Text
              style={styles[`statusError-${this.tt.themeName}`] ||= {
                color: this.tt.colors.dangerText,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="invoicesIndexScreen/status"
            >
              {status.text}
            </Text>
          }
          {loading && <LoadingState testID="invoicesIndexScreen/loadingText" />}
          {!loading && this.renderInvoicesList()}
          {this.renderPagination()}
        </View>
      </View>
    )
  }

  /**
   * Renders filter and sort controls for the invoice list.
   * @returns {ReactNode} Filter controls.
   */
  renderFilters() {
    const {
      creditorIds,
      dateFrom,
      dateTo,
      invoiceTypeFilter,
      organizationIds,
      searchQuery,
      sortKey,
      stateFilter
    } = this.s
    const breakpoint = this.tt.breakpoint
    const breakpointName = breakpoint.name
    const filtersActive = Boolean(
      searchQuery.trim() ||
      stateFilter ||
      invoiceTypeFilter ||
      creditorIds.length > 0 ||
      organizationIds.length > 0 ||
      dateFrom ||
      dateTo ||
      sortKey !== DEFAULT_SORT_KEY
    )
    return (
      <View
        style={styles[`filtersCard-${this.tt.themeName}`] ||= {
          backgroundColor: this.tt.colors.surface,
          borderColor: this.tt.colors.border,
          borderRadius: 8,
          borderWidth: 1,
          gap: 12,
          marginBottom: 16,
          padding: 16
        }}
        testID="invoicesIndexScreen/filters"
      >
        <View
          style={styles[`filterRow-${breakpointName}`] ||= breakpoint.styling({
            base: {
              alignItems: "stretch",
              flexDirection: "column",
              gap: 12
            },
            smUp: {
              alignItems: "flex-end",
              flexDirection: "row",
              flexWrap: "wrap"
            }
          })}
          testID="invoicesIndexScreen/filterControls"
        >
          <View
            style={styles[`searchField-${breakpointName}`] ||= breakpoint.styling({
              base: {
                minWidth: 0,
                width: "100%"
              },
              smUp: {
                flexBasis: "48%",
                flexGrow: 1,
                width: undefined
              },
              mdUp: {
                flexBasis: "31%"
              },
              lgUp: {
                flexBasis: "23%"
              },
              xlUp: {
                flexBasis: "23%"
              },
              xxlUp: {
                flexBasis: "23%"
              }
            })}
            testID="invoicesIndexScreen/filters/searchField"
          >
            <LabeledTextInput
              defaultValue={searchQuery}
              inputRef={this.tt.searchInputRef}
              label={_("Search")}
              onChangeText={this.tt.onSearchChangeText}
              placeholder={_("Search invoices...")}
              testID="invoicesIndexScreen/filters/searchInput"
            />
          </View>
          <View
            style={styles[`stateField-${breakpointName}`] ||= breakpoint.styling({
              base: {
                minWidth: 0,
                width: "100%"
              },
              smUp: {
                flexBasis: "48%",
                flexGrow: 1,
                width: undefined
              },
              mdUp: {
                flexBasis: "31%"
              },
              lgUp: {
                flexBasis: "23%"
              },
              xlUp: {
                flexBasis: "23%"
              },
              xxlUp: {
                flexBasis: "23%"
              }
            })}
            testID="invoicesIndexScreen/filters/stateField"
          >
            <LabeledSelect
              label={_("State")}
              onChangeValue={this.tt.onStateFilterChangeValue}
              options={invoiceStateOptions()}
              placeholder={_("All states")}
              testID="invoicesIndexScreen/filters/stateSelect"
              values={stateFilter ? [stateFilter] : []}
            />
          </View>
          <View
            style={styles[`typeField-${breakpointName}`] ||= breakpoint.styling({
              base: {
                minWidth: 0,
                width: "100%"
              },
              smUp: {
                flexBasis: "48%",
                flexGrow: 1,
                width: undefined
              },
              mdUp: {
                flexBasis: "31%"
              },
              lgUp: {
                flexBasis: "23%"
              },
              xlUp: {
                flexBasis: "23%"
              },
              xxlUp: {
                flexBasis: "23%"
              }
            })}
            testID="invoicesIndexScreen/filters/typeField"
          >
            <LabeledSelect
              label={_("Type")}
              onChangeValue={this.tt.onInvoiceTypeFilterChangeValue}
              options={invoiceTypeOptions()}
              placeholder={_("All invoice types")}
              testID="invoicesIndexScreen/filters/typeSelect"
              values={invoiceTypeFilter ? [invoiceTypeFilter] : []}
            />
          </View>
          <View
            style={styles[`creditorField-${breakpointName}`] ||= breakpoint.styling({
              base: {
                minWidth: 0,
                width: "100%"
              },
              smUp: {
                flexBasis: "48%",
                flexGrow: 1,
                width: undefined
              },
              mdUp: {
                flexBasis: "31%"
              },
              lgUp: {
                flexBasis: "23%"
              },
              xlUp: {
                flexBasis: "23%"
              },
              xxlUp: {
                flexBasis: "23%"
              }
            })}
            testID="invoicesIndexScreen/filters/creditorField"
          >
            <OrganizationsSelect
              creditor
              label={_("Creditor")}
              multiple
              onChangeValue={this.tt.onCreditorFilterChangeValue}
              placeholder={_("All creditors")}
              testID="invoicesIndexScreen/filters/creditorSelect"
              values={creditorIds.map(String)}
            />
          </View>
          <View
            style={styles[`debitorField-${breakpointName}`] ||= breakpoint.styling({
              base: {
                minWidth: 0,
                width: "100%"
              },
              smUp: {
                flexBasis: "48%",
                flexGrow: 1,
                width: undefined
              },
              mdUp: {
                flexBasis: "31%"
              },
              lgUp: {
                flexBasis: "23%"
              },
              xlUp: {
                flexBasis: "23%"
              },
              xxlUp: {
                flexBasis: "23%"
              }
            })}
            testID="invoicesIndexScreen/filters/debitorField"
          >
            <OrganizationsSelect
              customer
              label={_("Debitor")}
              multiple
              onChangeValue={this.tt.onDebitorFilterChangeValue}
              placeholder={_("All debitors")}
              testID="invoicesIndexScreen/filters/debitorSelect"
              values={organizationIds.map(String)}
            />
          </View>
          <View
            style={styles[`dateFromField-${breakpointName}`] ||= breakpoint.styling({
              base: {
                minWidth: 0,
                width: "100%"
              },
              smUp: {
                flexBasis: "48%",
                flexGrow: 1,
                width: undefined
              },
              mdUp: {
                flexBasis: "31%"
              },
              lgUp: {
                flexBasis: "23%"
              },
              xlUp: {
                flexBasis: "23%"
              },
              xxlUp: {
                flexBasis: "23%"
              }
            })}
            testID="invoicesIndexScreen/filters/dateFromField"
          >
            <LabeledTextInput
              defaultValue={dateFrom}
              inputRef={this.tt.dateFromInputRef}
              label={_("Date from")}
              onChangeText={this.tt.onDateFromChangeText}
              placeholder="YYYY-MM-DD"
              testID="invoicesIndexScreen/filters/dateFromInput"
            />
          </View>
          <View
            style={styles[`dateToField-${breakpointName}`] ||= breakpoint.styling({
              base: {
                minWidth: 0,
                width: "100%"
              },
              smUp: {
                flexBasis: "48%",
                flexGrow: 1,
                width: undefined
              },
              mdUp: {
                flexBasis: "31%"
              },
              lgUp: {
                flexBasis: "23%"
              },
              xlUp: {
                flexBasis: "23%"
              },
              xxlUp: {
                flexBasis: "23%"
              }
            })}
            testID="invoicesIndexScreen/filters/dateToField"
          >
            <LabeledTextInput
              defaultValue={dateTo}
              inputRef={this.tt.dateToInputRef}
              label={_("Date to")}
              onChangeText={this.tt.onDateToChangeText}
              placeholder="YYYY-MM-DD"
              testID="invoicesIndexScreen/filters/dateToInput"
            />
          </View>
          <View
            style={styles[`sortField-${breakpointName}`] ||= breakpoint.styling({
              base: {
                minWidth: 0,
                width: "100%"
              },
              smUp: {
                flexBasis: "48%",
                flexGrow: 1,
                width: undefined
              },
              mdUp: {
                flexBasis: "31%"
              },
              lgUp: {
                flexBasis: "23%"
              },
              xlUp: {
                flexBasis: "23%"
              },
              xxlUp: {
                flexBasis: "23%"
              }
            })}
            testID="invoicesIndexScreen/filters/sortField"
          >
            <LabeledSelect
              label={_("Sort")}
              onChangeValue={this.tt.onSortChangeValue}
              options={invoiceSortOptions()}
              testID="invoicesIndexScreen/filters/sortSelect"
              values={[sortKey]}
            />
          </View>
          <View
            style={styles[`clearField-${breakpointName}`] ||= breakpoint.styling({
              base: {
                alignSelf: "stretch",
                justifyContent: "center",
                width: "100%"
              },
              smUp: {
                alignSelf: "stretch",
                flexBasis: "48%",
                flexGrow: 1,
                justifyContent: "center",
                width: undefined
              },
              mdUp: {
                flexBasis: "31%"
              },
              lgUp: {
                flexBasis: "23%"
              },
              xlUp: {
                flexBasis: "23%"
              },
              xxlUp: {
                flexBasis: "23%"
              }
            })}
            testID="invoicesIndexScreen/filters/clearField"
          >
            <Button
              disabled={!filtersActive}
              fullWidth
              iconName="xmark"
              label={_("Clear filters")}
              onPress={this.tt.onClearFiltersPress}
              testID="invoicesIndexScreen/filters/clearButton"
              variant="outline"
            />
          </View>
        </View>
      </View>
    )
  }

  /**
   * Renders loaded invoices or an empty state.
   * @returns {ReactNode} Invoice list content.
   */
  renderInvoicesList() {
    const {invoices} = this.s
    return (
      <View
        dataSet={dataSets[`listState-${this.s.loadedFiltersKey}-${this.s.invoiceDebugState}`] ||= {
          debugState: this.s.invoiceDebugState,
          filtersKey: this.s.loadedFiltersKey
        }}
        testID="invoicesIndexScreen/list"
      >
        {invoices.length === 0 ?
          <EmptyState
            message={_("No invoices found")}
            testID="invoicesIndexScreen/emptyCard"
          />
        :
          invoices.map((invoice) =>
            <InvoiceCard
              invoice={invoice}
              key={invoice.id()}
              onDeletePress={this.tt.onDeletePress}
              testID={`invoicesIndexScreen/row-${invoice.id()}`}
            />
          )}
      </View>
    )
  }

  /**
   * Deletes an invoice after confirmation.
   * @param {Invoice} invoice Invoice selected for deletion.
   * @returns {Promise<void>} Resolves after the delete flow finishes.
   */
  onDeletePress = async (invoice) => {
    const invoiceId = invoice.id()
    const label = invoice.invoiceNo() || `#${invoiceId}`
    const ok = await confirmModal(
      _("Delete invoice: %{label}?", {label}),
      {
        cancelLabel: _("Cancel"),
        confirmLabel: _("Delete"),
        title: _("Confirm delete"),
        tone: "destructive"
      }
    )
    if (!ok) return
    try {
      await invoice.destroy()
      FlashNotifications.success(_("Invoice deleted"))
      const remaining = this.s.invoices.filter((candidate) => candidate.id() !== invoiceId)
      this.s.invoices = remaining
      this.s.totalCount = Math.max(0, this.s.totalCount - 1)
      if (remaining.length === 0 && this.s.page > 1) {
        this.s.page -= 1
      }
    } catch (error) {
      FlashNotifications.errorResponse(error)
    }
  }

  /**
   * Renders pagination for loaded invoices.
   * @returns {ReactNode} Pagination controls or null while loading.
   */
  renderPagination() {
    const {loading, page, totalCount} = this.s
    if (loading) return null
    return (
      <Pagination
        onPageChange={this.tt.onPageChange}
        page={page}
        pageSize={PAGE_SIZE}
        testID="invoicesIndexScreen/pagination"
        totalCount={totalCount}
      />
    )
  }

  /**
   * Applies a new page number.
   * @param {number} nextPage Page to load.
   * @returns {void} Nothing.
   */
  onPageChange = (nextPage) => {
    if (nextPage === this.s.page) return
    this.s.page = nextPage
  }

  /**
   * Debounces invoice search changes into the route.
   * @param {string} value Text typed in the invoice search field.
   * @returns {void} Nothing.
   */
  onSearchChangeText = (value) => {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
    this.searchDebounceTimer = setTimeout(() => {
      pushQueryParamUpdates(this.tt.router, this.tt.queryParamsForCurrentFilters(), "/invoices", {
        search: value || undefined
      })
      this.s.page = 1
      this.s.searchQuery = value
    }, SEARCH_DEBOUNCE_MS)
  }

  /**
   * Applies the selected invoice state filter.
   * @param {(string | number)[] | string | number | undefined} value Selected state value.
   * @returns {void} Nothing.
   */
  onStateFilterChangeValue = (value) => {
    const stateFilter = selectedValue(value)
    if (stateFilter === this.s.stateFilter) return
    pushQueryParamUpdates(this.tt.router, this.tt.queryParamsForCurrentFilters(), "/invoices", {
      state: stateFilter || undefined
    })
    this.s.page = 1
    this.s.stateFilter = stateFilter
  }

  /**
   * Applies the selected invoice type filter.
   * @param {(string | number)[] | string | number | undefined} value Selected type value.
   * @returns {void} Nothing.
   */
  onInvoiceTypeFilterChangeValue = (value) => {
    const invoiceTypeFilter = selectedValue(value)
    if (invoiceTypeFilter === this.s.invoiceTypeFilter) return
    pushQueryParamUpdates(this.tt.router, this.tt.queryParamsForCurrentFilters(), "/invoices", {
      invoice_type: invoiceTypeFilter || undefined
    })
    this.s.invoiceTypeFilter = invoiceTypeFilter
    this.s.page = 1
  }

  /**
   * Applies selected creditor filters.
   * @param {(string | number)[] | string | number | undefined} values Selected creditor values.
   * @returns {void} Nothing.
   */
  onCreditorFilterChangeValue = (values) => {
    const nextIds = identifierListParam(values, "Creditor IDs")
    if (sameIds(nextIds, this.s.creditorIds)) return
    pushQueryParamUpdates(this.tt.router, this.tt.queryParamsForCurrentFilters(), "/invoices", {
      creditor_ids: nextIds.length === 0 ? undefined : nextIds.map(String)
    })
    this.s.creditorIds = nextIds
    this.s.page = 1
  }

  /**
   * Applies selected debitor filters.
   * @param {(string | number)[] | string | number | undefined} values Selected debitor values.
   * @returns {void} Nothing.
   */
  onDebitorFilterChangeValue = (values) => {
    const nextIds = identifierListParam(values, "Organization IDs")
    if (sameIds(nextIds, this.s.organizationIds)) return
    pushQueryParamUpdates(this.tt.router, this.tt.queryParamsForCurrentFilters(), "/invoices", {
      organization_ids: nextIds.length === 0 ? undefined : nextIds.map(String)
    })
    this.s.organizationIds = nextIds
    this.s.page = 1
  }

  /**
   * Applies the start date filter.
   * @param {string} value Date value entered by the user.
   * @returns {void} Nothing.
   */
  onDateFromChangeText = (value) => {
    if (this.dateFromDebounceTimer) clearTimeout(this.dateFromDebounceTimer)
    this.dateFromDebounceTimer = setTimeout(() => {
      pushQueryParamUpdates(this.tt.router, this.tt.queryParamsForCurrentFilters(), "/invoices", {
        date_from: value || undefined
      })
      this.dateFromDebounceTimer = null
    }, SEARCH_DEBOUNCE_MS)
    this.s.dateFrom = value
    this.s.page = 1
  }

  /**
   * Applies the end date filter.
   * @param {string} value Date value entered by the user.
   * @returns {void} Nothing.
   */
  onDateToChangeText = (value) => {
    if (this.dateToDebounceTimer) clearTimeout(this.dateToDebounceTimer)
    this.dateToDebounceTimer = setTimeout(() => {
      pushQueryParamUpdates(this.tt.router, this.tt.queryParamsForCurrentFilters(), "/invoices", {
        date_to: value || undefined
      })
      this.dateToDebounceTimer = null
    }, SEARCH_DEBOUNCE_MS)
    this.s.dateTo = value
    this.s.page = 1
  }

  /**
   * Applies the selected invoice sort.
   * @param {(string | number)[] | string | number | undefined} value Selected sort value.
   * @returns {void} Nothing.
   */
  onSortChangeValue = (value) => {
    const sortKey = parseSortKey(selectedValue(value))
    if (sortKey === this.s.sortKey) return
    pushQueryParamUpdates(this.tt.router, this.tt.queryParamsForCurrentFilters(), "/invoices", {
      sort: sortKey === DEFAULT_SORT_KEY ? undefined : sortKey
    })
    this.s.page = 1
    this.s.sortKey = sortKey
  }

  /** Clears all route-backed filters and restores the default sort. */
  onClearFiltersPress = () => {
    if (this.dateFromDebounceTimer) clearTimeout(this.dateFromDebounceTimer)
    if (this.dateToDebounceTimer) clearTimeout(this.dateToDebounceTimer)
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
    this.dateFromDebounceTimer = null
    this.dateToDebounceTimer = null
    this.searchDebounceTimer = null
    syncTextInputValue(this.searchInputRef, "")
    syncTextInputValue(this.dateFromInputRef, "")
    syncTextInputValue(this.dateToInputRef, "")
    pushQueryParamUpdates(this.tt.router, this.tt.currentQueryParams(), "/invoices", {
      creditor_ids: undefined,
      date_from: undefined,
      date_to: undefined,
      invoice_type: undefined,
      organization_ids: undefined,
      search: undefined,
      sort: undefined,
      state: undefined
    })
    this.s.creditorIds = []
    this.s.dateFrom = ""
    this.s.dateTo = ""
    this.s.invoiceTypeFilter = ""
    this.s.organizationIds = []
    this.s.page = 1
    this.s.searchQuery = ""
    this.s.sortKey = DEFAULT_SORT_KEY
    this.s.stateFilter = ""
  }

  /**
   * Loads the current invoice page.
   * @returns {Promise<void>} Resolves when invoices finish loading.
   * @rejects {unknown} When loading fails.
   */
  loadInvoices = async () => {
    const filters = this.currentFilters()
    const filtersKey = serializeFilters(filters)
    const page = this.s.page
    const requestId = ++this.loadRequestId
    this.s.loading = true
    this.s.status = null
    try {
      const baseQuery = Invoice.ransack(this.ransackParamsForFilters(filters))
      const loadedInvoices = await baseQuery
        .clone()
        .preload({creditor: true, organization: true})
        .select(INVOICE_CARD_SELECT)
        .select(INVOICE_CARD_PRELOAD_SELECT)
        .page(page)
        .perPage(PAGE_SIZE)
        .toArray()
      if (this.loadRequestId !== requestId) return
      const invoices = this.loadedInvoicesForFilters(loadedInvoices, filters.searchQuery)
      this.s.loadedFiltersKey = filtersKey
      this.s.invoiceDebugState = JSON.stringify({
        filters,
        loadedIds: loadedInvoices.map((invoice) => String(invoice.id())),
        renderedIds: invoices.map((invoice) => String(invoice.id())),
        requestId
      })
      this.s.invoices = invoices
      this.s.loading = false
      this.s.totalCount = filters.searchQuery.trim() ? invoices.length : await baseQuery.clone().count()
    } catch (error) {
      if (this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }

  /**
   * Reads the current filter state as a stable plain object.
   * @returns {InvoicesIndexFilters} Snapshot used for the next invoice query.
   */
  currentFilters() {
    return {
      creditorIds: this.s.creditorIds,
      dateFrom: this.s.dateFrom,
      dateTo: this.s.dateTo,
      invoiceTypeFilter: this.s.invoiceTypeFilter,
      organizationIds: this.s.organizationIds,
      searchQuery: this.s.searchQuery,
      sortKey: this.s.sortKey,
      stateFilter: this.s.stateFilter
    }
  }

  /**
   * Builds query params from current filter state so pending debounced filters are preserved.
   * @returns {RouteQueryParams} Route query params.
   */
  queryParamsForCurrentFilters() {
    return {
      ...this.currentQueryParams(),
      creditor_ids: this.s.creditorIds.length === 0 ? undefined : this.s.creditorIds.map(String),
      date_from: this.s.dateFrom || undefined,
      date_to: this.s.dateTo || undefined,
      invoice_type: this.s.invoiceTypeFilter || undefined,
      organization_ids: this.s.organizationIds.length === 0 ? undefined : this.s.organizationIds.map(String),
      search: this.s.searchQuery || undefined,
      sort: this.s.sortKey === DEFAULT_SORT_KEY ? undefined : this.s.sortKey,
      state: this.s.stateFilter || undefined
    }
  }

  /**
   * Builds ransack parameters for non-search filters and sorting.
   * @param {InvoicesIndexFilters} filters Active filters.
   * @returns {Record<string, unknown>} Ransack query parameters.
   */
  ransackParamsForFilters(filters) {
    return buildRansackParams(filters)
  }

  /**
   * Applies the searched id set to loaded invoices before marking the list loaded.
   * @param {Invoice[]} invoices Records returned by the page query.
   * @param {string} searchQuery Search query to apply locally.
   * @returns {Invoice[]} Invoices safe to render for the current filters.
   */
  loadedInvoicesForFilters(invoices, searchQuery) {
    const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase()

    if (!normalizedSearchQuery) return invoices

    return invoices.filter((invoice) =>
      invoice.invoiceNo().toLocaleLowerCase().includes(normalizedSearchQuery) ||
      invoice.title().toLocaleLowerCase().includes(normalizedSearchQuery)
    )
  }
}

/** @type {NamedExoticComponent<InvoicesIndexScreenProps>} */
const InvoicesIndexScreenComponent = memo(shapeComponent(InvoicesIndexScreen))

export default InvoicesIndexScreenComponent

/**
 * Returns invoice state options for the state filter.
 * @returns {{text: string, value: string}[]} Select options.
 */
function invoiceStateOptions() {
  return [
    {text: _("Draft"), value: "draft"},
    {text: _("Finished"), value: "finished"},
    {text: _("Sent"), value: "sent"},
    {text: _("Paid"), value: "paid"}
  ]
}

/**
 * Returns invoice type options for the type filter.
 * @returns {{text: string, value: string}[]} Select options.
 */
function invoiceTypeOptions() {
  return [
    {text: _("Debit"), value: "debit"},
    {text: _("Credit"), value: "credit"},
    {text: _("Purchase"), value: "purchase"}
  ]
}

/**
 * Returns invoice sort options.
 * @returns {{text: string, value: string}[]} Select options.
 */
function invoiceSortOptions() {
  return [
    {text: _("Newest first"), value: "date_desc"},
    {text: _("Oldest first"), value: "date_asc"},
    {text: _("Invoice number A-Z"), value: "invoice_no_asc"},
    {text: _("Invoice number Z-A"), value: "invoice_no_desc"},
    {text: _("Highest amount"), value: "amount_desc"},
    {text: _("Lowest amount"), value: "amount_asc"},
    {text: _("State A-Z"), value: "state_asc"},
    {text: _("Type A-Z"), value: "type_asc"}
  ]
}

/**
 * Builds invoice list filters from route params.
 * @param {RouteQueryParams} params Route params to parse.
 * @returns {InvoicesIndexFilters} Parsed filter values.
 */
function filtersFromParams(params) {
  return {
    creditorIds: identifierListParam(params.creditor_ids, "Creditor IDs"),
    dateFrom: optionalRouteParam(params, "date_from", "Date from") ?? "",
    dateTo: optionalRouteParam(params, "date_to", "Date to") ?? "",
    invoiceTypeFilter: parseInvoiceTypeFilter(optionalRouteParam(params, "invoice_type", "Invoice type") ?? ""),
    organizationIds: identifierListParam(params.organization_ids, "Organization IDs"),
    searchQuery: optionalRouteParam(params, "search", "Search") ?? "",
    sortKey: parseSortKey(optionalRouteParam(params, "sort", "Sort") ?? ""),
    stateFilter: parseStateFilter(optionalRouteParam(params, "state", "State") ?? "")
  }
}

/**
 * Serializes filters into a stable comparison key.
 * @param {InvoicesIndexFilters} filters Filters to serialize.
 * @returns {string} Stable serialized key.
 */
function serializeFilters(filters) {
  return JSON.stringify({
    creditorIds: filters.creditorIds,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    invoiceTypeFilter: filters.invoiceTypeFilter,
    organizationIds: filters.organizationIds,
    searchQuery: filters.searchQuery,
    sortKey: filters.sortKey,
    stateFilter: filters.stateFilter
  })
}

/**
 * Builds ransack parameters for invoice list queries.
 * @param {InvoicesIndexFilters} filters Active filters.
 * @returns {Record<string, unknown>} Ransack query parameters.
 */
function buildRansackParams(filters) {
  /** @type {Record<string, unknown>} */
  const ransackParams = {
    s: sortExpression(filters.sortKey)
  }
  if (filters.creditorIds.length > 0) ransackParams.creditorIdIn = filters.creditorIds.map(String)
  if (filters.dateFrom) ransackParams.dateGteq = filters.dateFrom
  if (filters.dateTo) ransackParams.dateLteq = filters.dateTo
  if (filters.invoiceTypeFilter) ransackParams.invoiceTypeEq = filters.invoiceTypeFilter
  if (filters.organizationIds.length > 0) ransackParams.organizationIdIn = filters.organizationIds.map(String)
  if (filters.stateFilter) ransackParams.stateEq = filters.stateFilter
  return ransackParams
}

/**
 * Converts a sort key to a ransack sort expression.
 * @param {InvoiceSortKey} sortKey Active sort key.
 * @returns {string} Ransack sort expression.
 */
function sortExpression(sortKey) {
  if (sortKey === "amount_asc") return "amountCents asc, id asc"
  if (sortKey === "amount_desc") return "amountCents desc, id desc"
  if (sortKey === "date_asc") return "date asc, id asc"
  if (sortKey === "invoice_no_asc") return "invoiceNo asc, id asc"
  if (sortKey === "invoice_no_desc") return "invoiceNo desc, id desc"
  if (sortKey === "state_asc") return "state asc, id asc"
  if (sortKey === "type_asc") return "invoiceType asc, id asc"
  return "date desc, id desc"
}

/**
 * Compares two sorted identifier lists.
 * @param {string[]} a First identifier list.
 * @param {string[]} b Second identifier list.
 * @returns {boolean} True when both lists contain the same ids in order.
 */
function sameIds(a, b) {
  if (a.length !== b.length) return false
  for (let index = 0; index < a.length; index++) {
    if (a[index] !== b[index]) return false
  }
  return true
}

/**
 * Normalizes HayaSelect values into a single string value.
 * @param {(string | number)[] | string | number | undefined} value Raw select callback value.
 * @returns {string} Selected value, or an empty string.
 */
function selectedValue(value) {
  if (Array.isArray(value)) return value.length > 0 ? String(value[0]) : ""
  if (typeof value === "string" || typeof value === "number") return String(value)

  return ""
}

/**
 * Parses a route param into an invoice state filter.
 * @param {unknown} value Route parameter value.
 * @returns {string} State filter or blank.
 */
function parseStateFilter(value) {
  if (value === "draft" || value === "finished" || value === "sent" || value === "paid") return value
  return ""
}

/**
 * Parses a route param into an invoice type filter.
 * @param {unknown} value Route parameter value.
 * @returns {string} Invoice type filter or blank.
 */
function parseInvoiceTypeFilter(value) {
  if (value === "debit" || value === "credit" || value === "purchase") return value
  return ""
}

/**
 * Parses a route param into an invoice sort key.
 * @param {unknown} value Route parameter value.
 * @returns {InvoiceSortKey} Whitelisted option used by the invoice query.
 */
function parseSortKey(value) {
  if (
    value === "amount_asc" ||
    value === "amount_desc" ||
    value === "date_asc" ||
    value === "date_desc" ||
    value === "invoice_no_asc" ||
    value === "invoice_no_desc" ||
    value === "state_asc" ||
    value === "type_asc"
  ) {
    return value
  }
  return DEFAULT_SORT_KEY
}

/**
 * Syncs an uncontrolled visible text input to an external filter value.
 * @param {RefObject<NativeTextInput | null>} inputRef Input to update.
 * @param {string} value Value to show.
 * @returns {void} Nothing.
 */
function syncTextInputValue(inputRef, value) {
  const input = inputRef.current
  if (!input) return
  if (typeof input.setNativeProps === "function") {
    input.setNativeProps({
      text: value
    })
    return
  }
  if (!value && typeof input.clear === "function") {
    input.clear()
  }
}
